import { createTheme } from "@material-ui/core/styles";

// Tema MUI v4 do "layout Novo" (projeto 2026-10-21, ver PROMPT.md em
// D:\voicechat\novo layout) - completamente separado do tema Clássico
// em App.js. So' entra em uso dentro do shell Novo (ThemeProvider
// aninhado) - o Clássico nunca importa nada deste arquivo.
//
// Os tokens de cor/raio/altura abaixo vêm direto da tabela da seção 4
// do PROMPT.md. Onde o MUI tem um slot padrão pro valor (palette.warning,
// palette.success, etc.) ele é usado explicitamente (main/light fixos,
// não gerados por augmentColor) pra bater exatamente com o hex do
// mockup. O que não tem slot padrão (ex: primary-soft, surface-soft)
// fica em `theme.newLayoutTokens`.

export const newLayoutColors = {
  light: {
    bg: "#F4F4F8",
    surface: "#FFFFFF",
    surfaceSoft: "#F7F6FC",
    border: "#E6E5EE",
    borderInput: "#E0DEEA",
    divider: "#EDECF3",
    dividerStrong: "#F1F0F5",
    text: "#1B1A2E",
    text2: "#3F3D52",
    textMuted: "#5E5C70",
    textMuted2: "#6B6980",
    primary: "#4B2FD6",
    primaryDark: "#2A1890",
    primaryText: "#3A22B8",
    primarySoft: "#EEEAFE",
    primarySofter: "#F4F1FF",
    primarySofterBorder: "#D9D3FB",
    neutralChipText: "#4A485E",
    neutralChipBg: "#F1F0F5",
    focusRing: "#B9AEF5",
  },
  // Secao 7 do PROMPT.md: "crie a versao escura dos tokens... e me
  // mostre antes de finalizar" - valores abaixo seguem a orientacao
  // dada (fundo #121120, superficie #1C1B2E, texto #ECEBF5, mesmo roxo),
  // com os tons auxiliares (bordas, textos secundarios, chips) adaptados
  // pra manter contraste 4,5:1 minimo. Ainda nao validado visualmente -
  // tarefa de QA cobre essa revisao.
  dark: {
    bg: "#121120",
    surface: "#1C1B2E",
    surfaceSoft: "#211F36",
    border: "#2E2B48",
    borderInput: "#352F54",
    divider: "#2A2740",
    dividerStrong: "#312D4A",
    text: "#ECEBF5",
    text2: "#C9C6DC",
    textMuted: "#A6A2C0",
    textMuted2: "#8F8BAE",
    primary: "#6C52F0",
    primaryDark: "#4B2FD6",
    primaryText: "#B9AEF5",
    primarySoft: "#2A2353",
    primarySofter: "#231D45",
    primarySofterBorder: "#423A6E",
    neutralChipText: "#C9C6DC",
    neutralChipBg: "#272441",
    focusRing: "#B9AEF5",
  },
};

export const newLayoutHeights = {
  button: 40,
  input: 41,
  menuItem: 44,
  header: 64,
  touchTarget: 40,
};

export const newLayoutRadii = {
  card: 16,
  control: 10,
  chip: 6,
  pill: 14,
};

export const newLayoutSpacing = {
  page: 24,
  pageTop: 28,
  cardGap: 18,
};

let interFontLoaded = false;
// Carrega a fonte Inter so' quando o layout Novo esta ativo - o
// Classico continua em Roboto (default do MUI), sem custo extra pra
// quem nunca troca de layout.
export const loadInterFont = () => {
  if (interFontLoaded) return;
  interFontLoaded = true;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";
  document.head.appendChild(link);
};

export const createNewLayoutTheme = (mode = "light") => {
  const c = newLayoutColors[mode] || newLayoutColors.light;

  const theme = createTheme({
    // Achado 2026-10-22: telas Classicas reaproveitadas (MainContainer,
    // etc.) tem "overflowY: scroll" fixo e esperam "...theme.scrollbarStyles"
    // pra deixar essa barra fina/discreta (8px, cor primaria, sombra
    // suave) - igual App.js define pro tema Classico. Sem isso aqui, a
    // barra cai pro padrao nativo do navegador: grossa e sem estilo,
    // o que o usuario reportou como "incomodando" em Comercial/Contatos.
    scrollbarStyles: {
      "&::-webkit-scrollbar": { width: "8px", height: "8px" },
      "&::-webkit-scrollbar-thumb": {
        borderRadius: "8px",
        boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
        backgroundColor: c.primary,
      },
    },
    scrollbarStylesSoft: {
      "&::-webkit-scrollbar": { width: "8px" },
      "&::-webkit-scrollbar-thumb": {
        borderRadius: "8px",
        backgroundColor: c.border,
      },
    },
    palette: {
      type: mode,
      primary: { main: c.primary, dark: c.primaryDark, contrastText: "#FFFFFF" },
      background: { default: c.bg, paper: c.surface },
      text: { primary: c.text, secondary: c.textMuted },
      divider: c.divider,
      warning: { main: "#9A3F07", light: "#FFF1E6", contrastText: "#9A3F07" },
      success: { main: "#1B6B3A", light: "#EEF9F2", contrastText: "#FFFFFF" },
      info: { main: "#1E46A8", light: "#E8EEFF", contrastText: "#FFFFFF" },
      error: { main: "#B42318", light: "#FFF5F4", contrastText: "#FFFFFF" },
    },
    shape: {
      borderRadius: newLayoutRadii.card,
    },
    typography: {
      fontFamily: ["Inter", "system-ui", "sans-serif"].join(","),
      button: { textTransform: "none", fontWeight: 600 },
    },
    overrides: {
      MuiCssBaseline: {
        "@global": {
          body: { backgroundColor: c.bg },
        },
      },
      MuiPaper: {
        rounded: { borderRadius: newLayoutRadii.card },
        outlined: {
          borderRadius: newLayoutRadii.card,
          border: `1px solid ${c.border}`,
          boxShadow: "none",
        },
        elevation1: { boxShadow: "none" },
        elevation2: { boxShadow: "none" },
      },
      MuiButton: {
        root: {
          borderRadius: newLayoutRadii.control,
          textTransform: "none",
          minHeight: newLayoutHeights.button,
        },
        outlined: {
          borderColor: c.borderInput,
          color: c.text2,
          backgroundColor: c.surface,
        },
      },
      MuiIconButton: {
        root: {
          borderRadius: newLayoutRadii.control,
        },
      },
      // Achado 2026-10-22: varias telas Classicas reaproveitadas tal
      // como sao (Comercial, Contatos, Base de Conhecimento, ...) usam
      // <MainContainer> -> MUI <Container> sem passar maxWidth, que por
      // padrao trava em "lg" (1280px). No Classico isso nunca incomodou
      // porque a area de conteudo ja' era mais estreita; na area do
      // layout Novo (mais larga, sidebar mais fina) a tela reaproveitada
      // fica visualmente "pela metade", centralizada com sobra de
      // espaco vazio nas laterais. So' muda aqui dentro do tema Novo -
      // o Classico continua intocado (nunca importa este arquivo).
      //
      // Primeira tentativa (so' "maxWidth: none") nao bastou: o MUI v4
      // gera essas classes com o limite dentro de uma media query
      // ('@media (min-width:1280px)' etc.) que o merge do tema mantem
      // junto da nossa regra, e em telas de desktop (>=1280px, o caso
      // real relatado) a media query ainda ganhava por vir depois no
      // CSS gerado. "!important" garante que a nossa regra vence
      // independente da ordem.
      MuiContainer: {
        maxWidthLg: { maxWidth: "none !important" },
        maxWidthMd: { maxWidth: "none !important" },
        maxWidthSm: { maxWidth: "none !important" },
        maxWidthXs: { maxWidth: "none !important" },
      },
      MuiChip: {
        root: { borderRadius: newLayoutRadii.chip },
      },
      MuiOutlinedInput: {
        root: {
          borderRadius: newLayoutRadii.control,
          backgroundColor: c.surface,
          minHeight: newLayoutHeights.input,
        },
        notchedOutline: {
          borderRadius: newLayoutRadii.control,
          borderColor: c.borderInput,
        },
      },
      MuiDialog: {
        paper: { borderRadius: newLayoutRadii.card },
      },
      MuiTableCell: {
        root: { borderBottom: `1px solid ${c.dividerStrong}` },
      },
      MuiFocusVisible: {},
    },
    // Anel de foco visivel (secao 7 do PROMPT.md), aplicado via classe
    // global pra inputs/botoes nativos usados nas telas do layout Novo.
    props: {
      MuiButtonBase: { disableRipple: false },
    },
  });

  theme.newLayoutTokens = {
    ...c,
    heights: newLayoutHeights,
    radii: newLayoutRadii,
    spacing: newLayoutSpacing,
  };
  theme.mode = mode;

  return theme;
};

export default createNewLayoutTheme;
